import type { CirclesChart } from '@shared/circlesChart';
import type { CirclesParticleEmission } from './circlesRenderTypes';

/**
 * Colour utilities shared across every Circles draw module. Owns
 * hex parsing, alpha blending, the default combo palette, and the
 * within-combo digit lookup. Pure helpers — no canvas state or
 * renderer references, so any draw module can depend on this without
 * pulling in the orchestrator.
 */

interface RgbTuple {
  r: number;
  g: number;
  b: number;
}

/**
 * Memoised hex → RGB lookup. The set of distinct colours per session
 * is tiny (chart palette colours + a handful of fixed UI hexes), so
 * an uncapped `Map` stays in single-digit-entry territory while
 * eliminating regex + parseInt × 3 work on every per-circle /
 * per-particle colour call. `null` cached for malformed inputs so we
 * only warn once per bad hex.
 */
const rgbCache = new Map<string, RgbTuple | null>();

/**
 * Strict hex → `{r,g,b}` (0-255) parser, memoised. Returns `null`
 * for malformed input (non-`#RRGGBB` shape, non-hex digits) so
 * callers can fall back without ever flowing `NaN` into emitted CSS
 * strings. Mapper `[Colours]` sections from imported `.osu` files
 * occasionally carry malformed values; surfacing a console warning
 * helps catch new ones without crashing the renderer.
 */
function parseHexRgb(hex: string): RgbTuple | null {
  const cached = rgbCache.get(hex);
  if (cached !== undefined) {
    return cached;
  }
  const c = hex.startsWith('#') ? hex.slice(1) : hex;
  if (c.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(c)) {
    console.warn(`[CirclesRenderer] Malformed hex colour ignored: "${hex}"`);
    rgbCache.set(hex, null);
    return null;
  }
  const tuple: RgbTuple = {
    r: parseInt(c.slice(0, 2), 16),
    g: parseInt(c.slice(2, 4), 16),
    b: parseInt(c.slice(4, 6), 16),
  };
  rgbCache.set(hex, tuple);
  return tuple;
}

/**
 * Serialise an `{r,g,b}` tuple (0-255 channels) back to a `#rrggbb`
 * string. Inverse of {@link parseHexRgb}; channels are assumed already
 * integer-clamped to 0-255.
 */
function rgbToHex(rgb: RgbTuple): string {
  const channel = (n: number): string => n.toString(16).padStart(2, '0');
  return `#${channel(rgb.r)}${channel(rgb.g)}${channel(rgb.b)}`;
}

/**
 * Rec. 601 perceived luminance (0-255). Green is weighted ~5× blue and
 * ~2× red because that mirrors how the eye reads brightness — which is
 * exactly why a saturated purple, whose luminance is carried entirely by
 * its red+blue channels, looks far darker than its raw peak channel
 * suggests.
 */
function perceivedLuminance(rgb: RgbTuple): number {
  return 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
}

/**
 * Hue angle in degrees (0-360) for an RGB tuple, or -1 for a pure grey
 * (zero chroma → no meaningful hue). Standard HSL hue derivation.
 */
function hueDegrees(rgb: RgbTuple): number {
  const max = Math.max(rgb.r, rgb.g, rgb.b);
  const min = Math.min(rgb.r, rgb.g, rgb.b);
  const delta = max - min;
  if (delta === 0) {
    return -1;
  }
  let h: number;
  if (max === rgb.r) {
    h = ((rgb.g - rgb.b) / delta) % 6;
  } else if (max === rgb.g) {
    h = (rgb.b - rgb.r) / delta + 2;
  } else {
    h = (rgb.r - rgb.g) / delta + 4;
  }
  h *= 60;
  return h < 0 ? h + 360 : h;
}

/**
 * Convert a hex `#RRGGBB` colour and alpha to an `rgba()` string. Used
 * to fade approach rings, particle trails, and HUD halos without
 * touching the global alpha (which would also affect the shadow blur).
 *
 * Hot path — called multiple times per visible hit object every
 * frame. Parsing is memoised; the per-call work is reduced to one
 * Map.get + a template literal.
 */
export function hexToRgba(hex: string, alpha: number): string {
  const rgb = parseHexRgb(hex);
  if (!rgb) {
    return `rgba(128, 128, 128, ${alpha})`;
  }
  return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
}

/**
 * Multiply each RGB channel by `factor` (0..1 darkens, >1 lightens)
 * and return as `rgb()`. Used by the slider body to derive the dark
 * inner-fill colour from a combo colour without losing hue identity:
 * a darker version of the combo colour still reads as "the same
 * slider" but contrasts cleanly against the bright outer rim.
 */
export function shadeHex(hex: string, factor: number): string {
  const rgb = parseHexRgb(hex);
  if (!rgb) {
    return hex;
  }
  const r = Math.max(0, Math.min(255, Math.round(rgb.r * factor)));
  const g = Math.max(0, Math.min(255, Math.round(rgb.g * factor)));
  const b = Math.max(0, Math.min(255, Math.round(rgb.b * factor)));
  return `rgb(${r}, ${g}, ${b})`;
}

/**
 * Purple-readability band + luminance floor used by the second stage of
 * {@link ensureReadableHex}.
 *
 * The hue window [{@link PURPLE_HUE_MIN}, {@link PURPLE_HUE_MAX}] degrees
 * brackets the violet→magenta purples (heroes' `#764176` sits at ~300°)
 * while deliberately excluding the cooler blue-violets just below it
 * (`#8080c0` at ~240°, lavender `#9288ca` at ~249°): those carry enough
 * green+blue luminance to stay legible, so widening the band would
 * recolour colours that read fine. {@link PURPLE_MIN_LUMA} (0-255
 * perceived luminance) is set a hair under the palette's intentional
 * bright magenta (`#ff2bd6` ≈ 126) so vivid purples/magentas are left
 * exactly as authored and only genuinely-dark purples get lifted.
 */
const PURPLE_HUE_MIN = 265;
const PURPLE_HUE_MAX = 340;
const PURPLE_MIN_LUMA = 122;

/**
 * Make a mapper-authored combo / slider colour safe to read on the dark
 * Circles playfield, applying two hue-aware corrections in turn while
 * preserving hue identity. Returns the input unchanged when it already
 * reads clearly — the common case for vivid palettes.
 *
 * 1. **Peak-channel floor (`minChannel`)** — lifts near-black / very dark
 *    colours of ANY hue. Some imported `.osu` `[Colours]` sections
 *    declare pure black or near-black; a slider body or circle drawn in
 *    it is invisible against the dark playfield, making the chart
 *    unplayable. Pure black has no hue to preserve so it collapses to a
 *    neutral grey; tinted darks (e.g. `#200000` deep red) scale up
 *    proportionally to keep their hue identity.
 *
 * 2. **Purple luminance floor ({@link PURPLE_MIN_LUMA})** — purple/violet
 *    is the one hue that still "disappears" after step 1. Its luminance
 *    lives in the red+blue channels the eye weights least (see
 *    {@link perceivedLuminance}), so a purple clears the hue-blind
 *    peak-channel test easily yet still reads far darker than, say, an
 *    equally-"dark" red or green — and it tends to sit on cool,
 *    violet-tinted dark backgrounds (heroes pairs `#764176` with the
 *    violet playfield palette), losing luminance AND chroma contrast at
 *    once. We single out the purple band and lift it toward a minimum
 *    perceived luminance by scaling its channels (which keeps the hue
 *    intact unless a channel saturates at 255 — never the case for the
 *    palettes we ship). Every non-purple hue is left exactly as the
 *    mapper authored it.
 */
export function ensureReadableHex(hex: string, minChannel: number = 96): string {
  const rgb = parseHexRgb(hex);
  if (!rgb) {
    return '#808080';
  }

  let working: RgbTuple = rgb;

  // 1. Peak-channel floor — rescue very dark colours of any hue.
  const maxCh = Math.max(working.r, working.g, working.b);
  if (maxCh === 0) {
    // Pure black has no hue to preserve — collapse to a neutral grey.
    working = { r: minChannel, g: minChannel, b: minChannel };
  } else if (maxCh < minChannel) {
    const factor = minChannel / maxCh;
    working = {
      r: Math.min(255, Math.round(working.r * factor)),
      g: Math.min(255, Math.round(working.g * factor)),
      b: Math.min(255, Math.round(working.b * factor)),
    };
  }

  // 2. Purple luminance floor — lift ONLY dark purples/violets, the one
  // hue that stays invisible after the peak-channel pass.
  const hue = hueDegrees(working);
  if (hue >= PURPLE_HUE_MIN && hue <= PURPLE_HUE_MAX) {
    // An in-band hue means non-zero chroma, so luminance is always > 0
    // here — the division below needs no divide-by-zero guard.
    const luma = perceivedLuminance(working);
    if (luma < PURPLE_MIN_LUMA) {
      const factor = PURPLE_MIN_LUMA / luma;
      working = {
        r: Math.min(255, Math.round(working.r * factor)),
        g: Math.min(255, Math.round(working.g * factor)),
        b: Math.min(255, Math.round(working.b * factor)),
      };
    }
  }

  // Untouched colours round-trip to the exact input string (no needless
  // case-normalisation); only corrected ones are re-serialised.
  return working === rgb ? hex : rgbToHex(working);
}

/**
 * Fixed combo-colour cycle, used when a chart doesn't provide its own
 * `[Colours]`. Four deliberately well-separated hues so any neighbour
 * pair reads as a different colour against the playfield, making
 * combo group boundaries obvious.
 *
 * Order picked to maximise contrast at every step:
 *   cyan → magenta → yellow → green → cyan ...
 */
export const COMBO_PALETTE = ['#16e0ff', '#ff2bd6', '#ffd84d', '#3bff9d'] as const;

/**
 * Memoised, per-chart palette of already-readability-corrected combo
 * colours — the single source of combo colours for circles, sliders and
 * followpoints (via {@link comboColour}) AND the kiai burst particles.
 * With this cache the per-call work collapses to one WeakMap lookup + an
 * array index instead of re-running {@link ensureReadableHex} over the
 * palette on every visible circle every frame (and every burst particle).
 *
 * Mapper-defined `chart.comboColours` (the source `.osu` `[Colours]`)
 * win when present and non-empty; otherwise the built-in
 * {@link COMBO_PALETTE}. Both are run through {@link ensureReadableHex}
 * so dark / invisible mapper hues are corrected exactly once per chart.
 *
 * Keyed by chart instance via {@link WeakMap} so chart swaps (loop
 * wrap, replay, map change) drop the entry naturally without anyone
 * having to invalidate.
 */
const comboColourCache = new WeakMap<CirclesChart, ReadonlyArray<string>>();

export function readableComboPalette(chart: CirclesChart): ReadonlyArray<string> {
  const cached = comboColourCache.get(chart);
  if (cached) {
    return cached;
  }
  const source =
    chart.comboColours && chart.comboColours.length > 0
      ? chart.comboColours
      : COMBO_PALETTE;
  const readable = source.map((c) => ensureReadableHex(c));
  comboColourCache.set(chart, readable);
  return readable;
}

/**
 * Resolve the combo-group colour at the given combo index.
 *
 * Mapper-defined `chart.comboColours` (parsed from the source `.osu`
 * `[Colours]` section) wins when present and non-empty — the source
 * mapper picked those hues for a reason and overriding them would
 * stamp our own visual identity on every imported map.
 *
 * Otherwise we cycle through {@link COMBO_PALETTE} — the four
 * deliberately well-separated hues that drive the synthesised charts
 * and any imported map whose `[Colours]` section was empty or
 * malformed.
 */
export function comboColour(chart: CirclesChart, index: number): string {
  const palette = readableComboPalette(chart);
  // Robust modulo for negative indices — JS `%` keeps sign.
  return palette[((index % palette.length) + palette.length) % palette.length];
}

/**
 * Compute the within-combo position (1-based) for each hit object —
 * the digit osu! draws inside the circle to communicate stream order.
 *
 * `comboIndex` on the data side is a colour-group counter (all
 * objects in one combo share the same value), so it CANNOT serve as
 * the displayed digit. The renderer derives the within-combo index
 * by walking hit objects in beat order and resetting the counter on
 * `newCombo`.
 *
 * Memoisation keyed by chart instance keeps this cost amortised — we
 * compute the lookup once per chart, then index into the Map per
 * frame without re-walking.
 */
const withinComboCache = new WeakMap<CirclesChart, ReadonlyMap<string, number>>();
export function getWithinComboLookup(chart: CirclesChart): ReadonlyMap<string, number> {
  const cached = withinComboCache.get(chart);
  if (cached) {
    return cached;
  }
  const lookup = new Map<string, number>();
  // Sort defensively — chart payloads SHOULD be beat-sorted but a
  // future tool that re-serialises JSON could shuffle them.
  const sorted = [...chart.hitObjects].sort((a, b) => a.beat - b.beat);
  let counter = 0;
  for (const obj of sorted) {
    if (obj.newCombo) {
      counter = 1;
    } else {
      counter += 1;
    }
    lookup.set(obj.id, counter);
  }
  withinComboCache.set(chart, lookup);
  return lookup;
}

/**
 * Default colour for each emission type when the session doesn't pass
 * an explicit override. Mirrors the verdict vocabulary used by
 * floating texts, flashes, and shockwaves so the visual language
 * stays consistent: perfect = green, good = cyan, ok = amber, miss /
 * death = red, milestone = warm gold, tap = cyan accent.
 */
export function colourForEmission(type: CirclesParticleEmission['type']): string {
  switch (type) {
    case 'hit_perfect': {
      return '#3bff9d';
    }
    case 'hit_good': {
      return '#16e0ff';
    }
    case 'hit_ok': {
      // Warm amber so OK reads as "just barely landed it" — distinct
      // from green/blue (clean hit) and red (miss). Same vocabulary
      // osu! uses for its 50 judgement burst.
      return '#ffd84d';
    }
    case 'hit_miss': {
      return '#ff3b5c';
    }
    case 'death': {
      return '#ff3b5c';
    }
    case 'milestone': {
      return '#ffd84d';
    }
    case 'tap': {
      return '#16e0ff';
    }
    default: {
      return '#ffffff';
    }
  }
}
